/**
 * Version History Service
 * Manages assessment version snapshots, auto-save, and restoration
 * 
 * Uses the shared axios api instance which handles automatic token refresh
 */

import { api } from './api';

class VersionHistoryService {
  constructor() {
    this.autoSaveInterval = null;
    this.autoSaveIntervalMs = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Get version history for a survey
   */
  async getVersionHistory(surveyId) {
    try {
      const response = await api.get(`/api/surveys/${surveyId}/versions/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching version history:', error);
      throw error;
    }
  }

  /**
   * Get a specific version
   */
  async getVersion(surveyId, versionId) {
    try {
      const response = await api.get(`/api/surveys/${surveyId}/versions/${versionId}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching version:', error);
      throw error;
    }
  }

  /**
   * Create an auto-save version
   */
  async autoSave(surveyId, configuration, metadata = {}) {
    try {
      const response = await api.post(`/api/surveys/${surveyId}/versions/auto_save/`, {
        configuration,
        metadata: {
          ...metadata,
          timestamp: new Date().toISOString(),
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error auto-saving:', error);
      throw error;
    }
  }

  /**
   * Create a manual save version
   */
  async manualSave(surveyId, configuration, changeSummary, metadata = {}) {
    try {
      const response = await api.post(`/api/surveys/${surveyId}/versions/`, {
        configuration,
        change_summary: changeSummary,
        metadata,
        version_type: 'manual',
      });
      return response.data;
    } catch (error) {
      console.error('Error manually saving:', error);
      throw error;
    }
  }

  /**
   * Create a snapshot when assessment is renamed
   */
  async renameSnapshot(surveyId, configuration, oldName, newName, metadata = {}) {
    try {
      const response = await api.post(`/api/surveys/${surveyId}/versions/rename_snapshot/`, {
        configuration,
        old_name: oldName,
        new_name: newName,
        metadata,
      });
      return response.data;
    } catch (error) {
      console.error('Error creating rename snapshot:', error);
      throw error;
    }
  }

  /**
   * Publish a version
   */
  async publishVersion(surveyId, versionId, changeSummary) {
    try {
      const response = await api.post(`/api/surveys/${surveyId}/versions/${versionId}/publish/`, {
        change_summary: changeSummary,
      });
      return response.data;
    } catch (error) {
      console.error('Error publishing version:', error);
      throw error;
    }
  }

  /**
   * Restore a previous version
   */
  async restoreVersion(surveyId, versionId) {
    try {
      const response = await api.post(`/api/surveys/${surveyId}/versions/${versionId}/restore/`);
      return response.data;
    } catch (error) {
      console.error('Error restoring version:', error);
      throw error;
    }
  }

  /**
   * Compare two versions
   */
  async compareVersions(surveyId, version1Id, version2Id) {
    try {
      const response = await api.post(`/api/surveys/${surveyId}/versions/compare/`, {
        version_1_id: version1Id,
        version_2_id: version2Id,
      });
      return response.data;
    } catch (error) {
      console.error('Error comparing versions:', error);
      throw error;
    }
  }

  /**
   * Delete a version (only draft versions)
   */
  async deleteVersion(surveyId, versionId) {
    try {
      await api.delete(`/api/surveys/${surveyId}/versions/${versionId}/`);
      return true;
    } catch (error) {
      console.error('Error deleting version:', error);
      throw error;
    }
  }

  /**
   * Get the latest published version
   */
  async getLatestPublished(surveyId) {
    try {
      const response = await api.get(`/api/surveys/${surveyId}/versions/latest_published/`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null; // No published versions
      }
      console.error('Error fetching latest published version:', error);
      throw error;
    }
  }

  /**
   * Start auto-save timer
   * @param {Function} saveCallback - Function to call on auto-save (should return configuration)
   */
  startAutoSave(saveCallback) {
    if (this.autoSaveInterval) {
      this.stopAutoSave();
    }

    this.autoSaveInterval = setInterval(() => {
      try {
        saveCallback();
      } catch (error) {
        console.error('Auto-save callback error:', error);
      }
    }, this.autoSaveIntervalMs);

    console.debug('Auto-save started (every 5 minutes)');
  }

  /**
   * Stop auto-save timer
   */
  stopAutoSave() {
    if (this.autoSaveInterval) {
      clearInterval(this.autoSaveInterval);
      this.autoSaveInterval = null;
      console.debug('Auto-save stopped');
    }
  }

  /**
   * Get time since last auto-save
   */
  getTimeSinceLastSave(lastSaveTime) {
    if (!lastSaveTime) return 'Never';
    
    const now = new Date();
    const then = new Date(lastSaveTime);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 minute ago';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return '1 hour ago';
    if (diffHours < 24) return `${diffHours} hours ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays} days ago`;
  }
}

export default new VersionHistoryService();
